import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Admin, AdminRole, createAdmin, deleteAdmin, listAdmins } from '../services/auth';
import AnimatedSection from './AnimatedSection';

const inputClass =
  'w-full rounded-lg border border-surface-border bg-surface px-4 py-3 text-white placeholder:text-muted/50 focus:outline-none focus:border-primary-500';

/** Generates a readable starter password that satisfies the 8-char minimum. */
const suggestPassword = (): string => {
  const words = ['Zoxohu', 'Nexa', 'Lagos', 'Abuja', 'Kitchen', 'Service', 'Orbit', 'Harvest'];
  const bytes = new Uint32Array(2);
  crypto.getRandomValues(bytes);
  const word = words[bytes[0] % words.length];
  return `${word}@${(bytes[1] % 9000) + 1000}`;
};

const emptyForm = {
  name: '',
  email: '',
  title: '',
  password: '',
  role: 'admin' as AdminRole,
  sendInvite: true,
};

const AdminManagement: React.FC = () => {
  const { admin } = useAuth();

  const [admins, setAdmins] = useState<Admin[]>([]);
  const [form, setForm] = useState({ ...emptyForm, password: suggestPassword() });
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setAdmins(await listAdmins());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load admins');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setWarning(null);

    if (!form.name.trim() || !form.email.trim() || !form.password) {
      setError('Name, email and a starter password are all required.');
      return;
    }
    if (form.password.length < 8) {
      setError('The starter password must be at least 8 characters.');
      return;
    }

    try {
      setCreating(true);
      const result = await createAdmin({
        name: form.name.trim(),
        email: form.email.trim(),
        title: form.title.trim() || undefined,
        password: form.password,
        role: form.role,
        sendInvite: form.sendInvite,
      });

      if (!form.sendInvite) {
        setNotice(`${result.admin.email} can now sign in. Send them their password yourself.`);
      } else if (result.inviteSent) {
        setNotice(`Invite sent to ${result.admin.email} with their sign-in details.`);
      } else {
        // The account exists either way — say so plainly so nobody creates it twice.
        setWarning(
          `${result.admin.email} was created, but the invite email did not send` +
            `${result.inviteError ? `: ${result.inviteError}` : '.'} ` +
            'Pass the password on another way, or add it again after fixing your mail settings.'
        );
      }

      setForm({ ...emptyForm, password: suggestPassword() });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the admin');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (target: Admin) => {
    if (!window.confirm(`Remove ${target.email}? They lose access immediately.`)) return;
    setError(null);
    setNotice(null);
    setWarning(null);
    try {
      await deleteAdmin(target.id);
      setNotice(`${target.email} removed.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the admin');
    }
  };

  return (
    <section className="py-10 md:py-14">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection animation="slide-up">
          <div className="mb-8">
            <h1 className="font-display text-2xl md:text-3xl font-semibold text-white mb-2">
              Admins
            </h1>
            <p className="text-muted text-sm">
              Add a teammate and they get an email with their sign-in details.
            </p>
          </div>
        </AnimatedSection>

        {error && (
          <div className="mb-6 rounded-lg border border-red-500/40 bg-red-500/10 text-red-400 px-4 py-3 text-sm">
            {error}
          </div>
        )}
        {warning && (
          <div className="mb-6 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-300 px-4 py-3 text-sm">
            {warning}
          </div>
        )}
        {notice && !error && !warning && (
          <div className="mb-6 rounded-lg border border-primary-500/40 bg-primary-500/10 text-primary-400 px-4 py-3 text-sm">
            {notice}
          </div>
        )}

        {!admin?.hasSavedMailPassword && (
          <div className="mb-6 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-300 px-4 py-3 text-sm">
            Invites are sent from your own Zoho mailbox, and you have no mail password saved.{' '}
            <Link to="/admin/account" className="underline">
              Save it in Account
            </Link>{' '}
            first, or uncheck "email them an invite" below.
          </div>
        )}

        {/* Add form */}
        <AnimatedSection animation="slide-up">
          <div className="rounded-2xl border border-surface-border bg-surface-card p-6 md:p-8 mb-6">
            <h2 className="text-white font-medium mb-6">Add an admin</h2>

            <form onSubmit={handleCreate} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-muted mb-2">Full name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Precious Okoro"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted mb-2">Email *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="them@nexasolutra.com"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted mb-2">Title</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Marketing Executive"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted mb-2">Role</label>
                  <select
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value as AdminRole })}
                    className={inputClass}
                  >
                    <option value="admin">Admin</option>
                    <option value="marketer">Business admin (marketer)</option>
                    <option value="superadmin">Super admin</option>
                  </select>
                  <p className="mt-2 text-xs text-muted">
                    A business admin works the partner pipeline only — no portfolio, blog
                    or commission access.
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm text-muted mb-2">Starter password *</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    autoComplete="new-password"
                    className={inputClass}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="px-3 py-2 rounded-lg border border-surface-border text-sm text-muted hover:text-white whitespace-nowrap"
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, password: suggestPassword() })}
                      className="px-3 py-2 rounded-lg border border-surface-border text-sm text-muted hover:text-white whitespace-nowrap"
                    >
                      Regenerate
                    </button>
                  </div>
                </div>
                <p className="text-xs text-muted mt-2">
                  They are prompted to change this the first time they sign in.
                </p>
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.sendInvite}
                  onChange={(e) => setForm({ ...form, sendInvite: e.target.checked })}
                  className="accent-primary-500 w-4 h-4 mt-0.5"
                />
                <span className="text-sm text-muted">
                  Email them an invite with the sign-in link, their email and this password — sent
                  from your mailbox ({admin?.zohoEmail || admin?.email}).
                </span>
              </label>

              <button type="submit" disabled={creating} className="btn-primary disabled:opacity-60">
                {creating ? 'Creating…' : form.sendInvite ? 'Create & send invite' : 'Create admin'}
              </button>
            </form>
          </div>
        </AnimatedSection>

        {/* Existing admins */}
        <AnimatedSection animation="slide-up" delay={80}>
          <div className="rounded-2xl border border-surface-border bg-surface-card p-6 md:p-8">
            <h2 className="text-white font-medium mb-6">
              Team {!loading && <span className="text-muted text-sm">({admins.length})</span>}
            </h2>

            {loading ? (
              <p className="text-muted text-sm">Loading…</p>
            ) : (
              <div className="rounded-xl border border-surface-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[520px]">
                    <tbody>
                      {admins.map((a) => (
                        <tr key={a.id} className="border-t border-surface-border first:border-t-0">
                          <td className="py-3 px-4">
                            <p className="text-white">{a.name}</p>
                            <p className="text-muted text-xs">{a.email}</p>
                          </td>
                          <td className="py-3 px-4 text-muted text-xs">{a.title || '—'}</td>
                          <td className="py-3 px-4">
                            <span
                              className={`px-2 py-0.5 rounded text-xs border whitespace-nowrap ${
                                a.role === 'superadmin'
                                  ? 'border-primary-500/40 bg-primary-500/10 text-primary-400'
                                  : 'border-surface-border text-muted'
                              }`}
                            >
                              {a.role === 'superadmin'
                                ? 'super admin'
                                : a.role === 'marketer'
                                ? 'business admin'
                                : 'admin'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {a.mustChangePassword && (
                              <span className="text-amber-400/90 text-xs whitespace-nowrap">
                                not signed in yet
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {a.id === admin?.id ? (
                              <span className="text-xs text-muted">you</span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleDelete(a)}
                                className="text-xs text-red-400 hover:text-red-300"
                              >
                                Remove
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default AdminManagement;
